import React from 'react'
import { router } from '@inertiajs/react'
import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Trash2, Copy, Check } from 'lucide-react'

interface WebhookData {
  id: string
  url: string
  description: string | null
  subscribed_events: string[]
  active: boolean
  application_name: string
  application_id: string
  created_at: string
}

interface EventData {
  id: string
  event_type: string
  status: string
  response_code: number | null
  attempts: number
  created_at: string
}

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  webhook: WebhookData
  events: EventData[]
  webhook_secret: string | null
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'delivered':
      return 'default' as const
    case 'failed':
      return 'destructive' as const
    default:
      return 'secondary' as const
  }
}

function SecretDisplay({ secret }: { secret: string }) {
  const [copied, setCopied] = React.useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 rounded bg-muted px-3 py-2 font-mono text-xs break-all">
        {secret}
      </code>
      <Button variant="ghost" size="icon" onClick={handleCopy}>
        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

export default function Show({ user, organization, webhook, events, webhook_secret }: Props) {
  function handleDelete() {
    router.delete(`/dashboard/webhooks/${webhook.id}`)
  }

  return (
    <AppShell user={user} organization={organization}>
      <div className="space-y-8">
        <PageHeader
          eyebrow="Webhooks · detail"
          title="Webhook Details"
          description={
            <span className="font-mono text-sm break-all">{webhook.url}</span>
          }
          actions={
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete this webhook endpoint and all its delivery history. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          }
        />

        {webhook_secret && (
          <section className="surface-card relative overflow-hidden p-6">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at top right, hsl(38 95% 55% / 0.12) 0%, transparent 60%)',
              }}
            />
            <div className="relative">
              <h2 className="font-heading text-base font-semibold tracking-tight text-amber-600 dark:text-amber-400">
                Webhook Signing Secret
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                This secret is shown only once. Save it now to verify webhook signatures.
              </p>
              <div className="mt-4">
                <SecretDisplay secret={webhook_secret} />
              </div>
            </div>
          </section>
        )}

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Configuration
          </h2>
          <div className="mt-5 space-y-5">
            <div className="grid grid-cols-2 gap-5 text-sm">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Status</p>
                <Badge variant={webhook.active ? 'default' : 'secondary'} className="mt-1">
                  {webhook.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Application</p>
                <p className="mt-1 font-medium">{webhook.application_name}</p>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Created</p>
                <p className="mt-1 font-medium">{new Date(webhook.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Description</p>
                <p className="mt-1 font-medium">{webhook.description || '—'}</p>
              </div>
            </div>
            <div>
              <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Subscribed events
              </p>
              <div className="flex flex-wrap gap-2">
                {webhook.subscribed_events.map((evt) => (
                  <Badge key={evt} variant="outline">{evt}</Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="surface-card overflow-hidden p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Delivery Log
          </h2>
          <div className="mt-5">
            {events.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No deliveries yet. Events will appear here once triggered.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-foreground/[0.06]">
                      <th className="py-2 pr-4 text-left font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Event</th>
                      <th className="py-2 pr-4 text-left font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Status</th>
                      <th className="py-2 pr-4 text-left font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Code</th>
                      <th className="py-2 pr-4 text-left font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Attempts</th>
                      <th className="py-2 text-left font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id} className="border-b border-foreground/[0.04] last:border-0">
                        <td className="py-2.5 pr-4 font-mono text-xs">{event.event_type}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant={statusBadgeVariant(event.status)}>
                            {event.status}
                          </Badge>
                        </td>
                        <td className="py-2.5 pr-4">{event.response_code || '—'}</td>
                        <td className="py-2.5 pr-4">{event.attempts}</td>
                        <td className="py-2.5 text-muted-foreground">
                          {new Date(event.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  )
}
